package com.klantenstop.web.pages.company

import com.klantenstop.web.auth.AuthModule
import com.materialkobweb.components.toast.ToastManager
import com.klantenstop.web.data.models.domain.Company
import com.klantenstop.web.data.models.domain.Status
import com.klantenstop.web.data.models.dto.requests.reviews.InsertReview
import com.klantenstop.web.data.models.dto.requests.reviews.UpdateReview
import com.klantenstop.web.data.models.dto.requests.status.InsertStatus
import com.klantenstop.web.data.repos.CategoryRepo
import com.klantenstop.web.data.repos.CompanyRepo
import com.klantenstop.web.data.repos.ReviewRepo
import com.klantenstop.web.data.repos.StatusRepo
import com.klantenstop.web.utils.AnalyticsManager
import com.klantenstop.web.utils.MVI
import com.materialkobweb.utils.ViewModel
import com.klantenstop.web.utils.catchWithCancellation
import com.materialkobweb.utils.Logger
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.async
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext

class CompanyViewModel(
    private val companyIdentifier: String,
    private val categoryRepo: CategoryRepo = CategoryRepo,
    private val companyRepo: CompanyRepo = CompanyRepo,
    private val statusRepo: StatusRepo = StatusRepo,
    private val reviewRepo: ReviewRepo = ReviewRepo,
) : ViewModel(), MVI<CompanyContract.State, CompanyContract.Intent, CompanyContract.Effect> {

    private val _state = MutableStateFlow(CompanyContract.State(companyIdentifier))
    override val state: StateFlow<CompanyContract.State> = _state.asStateFlow()

    private val _effect = MutableSharedFlow<CompanyContract.Effect>()
    override val effect: SharedFlow<CompanyContract.Effect> = _effect.asSharedFlow()

    init {
        sendIntent(CompanyContract.Intent.LoadInitialData)
    }

    /**
     * Keeps review state in sync with auth changes. Must be collected from the page's composition
     * (LaunchedEffect) so it stops when the page leaves the backstack view, even though this
     * cached ViewModel stays alive.
     */
    suspend fun observeAuthStatus() {
        AuthModule.authStatus
            .filterNotNull()
            .map { it.authenticated }
            .distinctUntilChanged()
            .collectLatest { isAuthenticated ->
                if (isAuthenticated) {
                    loadCurrentUserReview()
                } else {
                    updateState {
                        it.copy(
                            myReview = null,
                            showCreateCompanyReviewDialog = false,
                            showUpdateCompanyStatusDialog = false,
                            showLoginRequiredDialog = false,
                            isSavingReview = false
                        )
                    }
                }
            }
    }

    override fun sendIntent(intent: CompanyContract.Intent) = coroutineScope.launch {
        when (intent) {
            is CompanyContract.Intent.LoadInitialData -> loadInitialData(companyIdentifier)
            is CompanyContract.Intent.HideUpdateCompanyStatusDialog -> updateState {
                it.copy(
                    showUpdateCompanyStatusDialog = false,
                    showStatusDescription = false,
                    hasAttemptedStatusSave = false
                )
            }

            is CompanyContract.Intent.ShowUpdateCompanyStatusDialog -> {
                if (AuthModule.authStatus.value?.authenticated == true) {
                    updateState {
                        val existingText = it.company?.status?.text.orEmpty()
                        it.copy(
                            showUpdateCompanyStatusDialog = true,
                            selectedStatusType = it.company?.status?.status ?: Status.Type.Unknown,
                            statusDescription = existingText,
                            showStatusDescription = existingText.isNotBlank(),
                            hasAttemptedStatusSave = false
                        )
                    }
                } else {
                    updateState { it.copy(showLoginRequiredDialog = true) }
                }
            }

            is CompanyContract.Intent.HideCreateCompanyReviewDialog -> updateState { it.copy(showCreateCompanyReviewDialog = false) }
            is CompanyContract.Intent.ShowCreateCompanyReviewDialog -> {
                if (AuthModule.authStatus.value?.authenticated == true) {
                    updateState { it.copy(showCreateCompanyReviewDialog = true) }
                } else {
                    updateState { it.copy(showLoginRequiredDialog = true) }
                }
            }

            is CompanyContract.Intent.ShowLoginRequiredDialog -> updateState { it.copy(showLoginRequiredDialog = true) }
            is CompanyContract.Intent.HideLoginRequiredDialog -> updateState { it.copy(showLoginRequiredDialog = false) }
            is CompanyContract.Intent.UpdateCompanyStatus -> updateState { it.copy(selectedStatusType = intent.companyStatus) }
            is CompanyContract.Intent.UpdateCompanyStatusDescription -> updateState { it.copy(statusDescription = intent.statusDescription) }
            is CompanyContract.Intent.ToggleStatusDescriptionVisibility -> updateState {
                val nowVisible = !it.showStatusDescription
                it.copy(
                    showStatusDescription = nowVisible,
                    statusDescription = if (nowVisible) it.statusDescription else ""
                )
            }

            is CompanyContract.Intent.SaveCompanyStatusUpdate -> saveCompanyStatus()
            is CompanyContract.Intent.SaveCompanyReview -> saveCompanyReview(intent.rating, intent.reviewText)
        }
    }

    private suspend fun saveCompanyStatus() {
        val companyId = currentCompanyIdOrNull() ?: run {
            Logger.warn("CompanyViewModel", "Cannot update status: unresolved company ID")
            return
        }

        updateState { it.copy(hasAttemptedStatusSave = true) }
        val currentState = state.value

        val newStatus = catchWithCancellation(
            block = {
                statusRepo.createNewStatus(
                    InsertStatus(
                        companyId = companyId,
                        status = currentState.selectedStatusType,
                        text = currentState.statusDescription
                    )
                )
            },
            onError = { e ->
                ToastManager.error(
                    e.message ?: "Er is een fout opgetreden bij het bijwerken van de status",
                    title = "Opslaan mislukt"
                )
            }
        )

        if (newStatus != null) {
            updateState {
                it.copy(
                    company = it.company?.copy(status = newStatus),
                    showUpdateCompanyStatusDialog = false,
                    selectedStatusType = Status.Type.Unknown,
                    statusDescription = "",
                    showStatusDescription = false,
                    hasAttemptedStatusSave = false
                )
            }
            ToastManager.success("Status succesvol bijgewerkt", title = "Gelukt")
        }
    }

    private suspend fun saveCompanyReview(rating: Int, reviewText: String) {
        val companyId = currentCompanyIdOrNull() ?: run {
            Logger.warn("CompanyViewModel", "Cannot save review: unresolved company ID")
            return
        }

        val currentReview = state.value.myReview

        // Validate rating
        if (rating == 0) {
            ToastManager.warning("Selecteer een beoordeling (sterren)", title = "Ongeldige invoer")
            return
        }

        if (reviewText.isBlank()) {
            ToastManager.warning("Vul een reviewtekst in", title = "Ongeldige invoer")
            return
        }

        updateState { it.copy(isSavingReview = true) }

        val newReview = catchWithCancellation(
            block = {
                if (currentReview != null) {
                    reviewRepo.updateReview(
                        currentReview.id,
                        UpdateReview(rating = rating, text = reviewText)
                    )
                } else {
                    reviewRepo.createReview(
                        companyId,
                        InsertReview(rating = rating, text = reviewText)
                    )
                }
            },
            onError = { e ->
                val errorMessage = e.message ?: if (currentReview != null) {
                    "Er is een fout opgetreden bij het bijwerken van je review"
                } else {
                    "Er is een fout opgetreden bij het aanmaken van je review"
                }
                ToastManager.error(errorMessage, title = "Opslaan mislukt")
            }
        )

        if (newReview != null) {
            // Fetch updated review total with error handling
            val reviewTotal = catchWithCancellation(
                block = { reviewRepo.getReviewTotal(companyId) },
                onError = { e ->
                    // Log error but don't block success flow - we'll use the old reviewTotal
                    Logger.warn("CompanyViewModel", "Failed to fetch updated review total: ${e.message}")
                }
            )

            updateState {
                it.copy(
                    myReview = newReview,
                    reviewTotal = reviewTotal ?: it.reviewTotal, // Keep existing if fetch failed
                    showCreateCompanyReviewDialog = false,
                    isSavingReview = false
                )
            }

            val successMessage = if (currentReview != null) {
                "Review succesvol bijgewerkt"
            } else {
                "Review succesvol aangemaakt"
            }
            ToastManager.success(successMessage, title = "Gelukt")

            val eventName = if (currentReview != null) {
                AnalyticsManager.Events.REVIEW_UPDATED
            } else {
                AnalyticsManager.Events.REVIEW_SUBMITTED
            }
            AnalyticsManager.trackEvent(
                eventName,
                mapOf(
                    "company_id" to companyId,
                    "rating" to rating
                )
            )
        } else {
            // Ensure isSavingReview is reset when review save failed
            updateState { it.copy(isSavingReview = false) }
        }
    }

    private suspend fun loadCurrentUserReview() {
        val companyId = currentCompanyIdOrNull() ?: return

        catchWithCancellation(
            block = {
                val myReview = reviewRepo.getMyReviewByCompanyId(companyId)
                updateState { it.copy(myReview = myReview) }
            }
            // Silent failure - not critical for page load
        )
    }

    private suspend fun loadInitialData(companyIdentifier: String) = withContext(Dispatchers.Default) {
        catchWithCancellation(
            block = {
                // Fetch company first to determine if it exists
                val company = companyRepo.getCompany(companyIdentifier)

                if (company == null) {
                    handleCompanyNotFound()
                } else {
                    loadCompanyData(company)
                }
            },
            onError = { e ->
                ToastManager.error(
                    e.message ?: "Er is een fout opgetreden bij het laden van de bedrijfsgegevens. Probeer het later opnieuw.",
                    title = "Laden mislukt"
                )
                updateState { it.copy(loading = false) }
            }
        )
    }

    private suspend fun handleCompanyNotFound() {
        val categories = catchWithCancellation(
            block = { categoryRepo.getCategories() }
        ) ?: emptyList()

        updateState {
            it.copy(
                companyNotFound = true,
                categories = categories,
                loading = false
            )
        }
    }

    private suspend fun loadCompanyData(company: Company) = withContext(Dispatchers.Default) {
        val reviewsDeferred = async { reviewRepo.getReviewsByCompanyId(company.id) }
        val reviewTotalDeferred = async { reviewRepo.getReviewTotal(company.id) }
        val categoriesDeferred = async { categoryRepo.getCategories() }

        val reviews = reviewsDeferred.await()
        val reviewTotal = reviewTotalDeferred.await()
        val categories = categoriesDeferred.await()

        updateState {
            it.copy(
                company = company,
                reviews = reviews,
                reviewTotal = reviewTotal,
                categories = categories,
                loading = false,
                companyNotFound = false
            )
        }

        AnalyticsManager.trackEvent(
            AnalyticsManager.Events.COMPANY_VIEWED,
            mapOf(
                "company_id" to company.id,
                "company_name" to company.name,
                "category" to (company.categories.firstOrNull()?.name ?: ""),
                "review_count" to reviewTotal.total,
                "avg_rating" to reviewTotal.average
            )
        )

        if (AuthModule.authStatus.value?.authenticated == true) {
            loadCurrentUserReview()
        }
    }

    private fun currentCompanyIdOrNull(): String? = state.value.company?.id

    override fun emitEffect(effect: CompanyContract.Effect) = coroutineScope.launch {
        _effect.emit(effect)
    }

    override fun updateState(block: (CompanyContract.State) -> CompanyContract.State) {
        _state.update(block)
    }
}
